import { observer } from 'mobx-react-lite'
import React from 'react'
import { addSeconds } from 'date-fns/addSeconds'
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths'

import { DateRange } from '@mui/x-date-pickers-pro'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { ArrowBack } from '@mxenabled/mx-icons'

import {
  ANALYTICS_EVENTS,
  ConnectDrawer,
  Drawer,
  EmptyState,
  Loader,
  WidgetContainer,
  useAccountStore,
  useAppConfig,
  useCategoryStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalStore,
  useGlobalUiStore,
  useInsightsEnabled,
  useScreenSize,
  useTransactionStore,
  useTrendsStore,
  useWidgetLoadTimer,
} from '../common'
import { type Beat, type DetailedCategoryWithTransactions, Transaction, WidgetProps } from '../core'

import DateRangeText from './components/DateRangeText'
import TrendsViewToggle, { type TrendsTabValue } from './components/TrendsViewToggle'
import TrendsTableContainer from './components/TrendsTableContainer'
import CategoriesList from './components/CategoriesList'
import InsightsCarousel from './components/TrendsInsights'
import { CategoryDetails } from './components/CategoryDetails'
import TrendsChartTotals from './components/TrendsChartTotals'
import { TRENDS_ANALYTICS_METADATA } from './constants/TrendsWidget'
import { TRENDS_BEAT_TEMPLATES } from './constants/TrendsInsights'
import { buildEmptyStateProps } from './utils/TrendsWidget'

interface TrendsWidgetProps extends WidgetProps {
  onInsightCardClick?: (beat?: Beat) => void
}

const TrendsWidget: React.FC<TrendsWidgetProps> = ({
  onBackClick,
  onMenuClick,
  onInsightCardClick,
  sx,
}) => {
  const { onEvent } = useEvent()
  const { config } = useAppConfig()
  const { isDesktop, isLargeDesktop, isMobile } = useScreenSize()
  const { isAccountDataLoaded, loadAccountData, visibleAccounts: accounts } = useAccountStore()
  const {
    categoriesLoaded,
    loadCategories,
    monthlyCategoryTotals,
    monthlyTotalsLoaded,
    reloadCategoryTotals,
  } = useCategoryStore()
  const { isTransactionDataLoaded, loadTransactionData, setFilter } = useTransactionStore()
  const { selectedCategoryData, selectedDateRange, setSelectedCategoryData, setSelectedDateRange } =
    useTrendsStore()
  const { isInitialized, selectedAccounts, selectedAccountGuids } = useGlobalUiStore()
  const { trends: copy, connect: connectCopy } = useGlobalCopyStore()
  const { beatStore } = useGlobalStore()
  const insightsEnabled = useInsightsEnabled()

  // allows clients to disable insights in trends while still enabling insights
  const showInsights = insightsEnabled && config.show_insights_widget_in_master
  const isLargeLayout = isDesktop || isLargeDesktop

  const [isWidgetInitialized, setIsWidgetInitialized] = React.useState(false)
  const [isBeatsLoaded, setIsBeatsLoaded] = React.useState(false)
  const [selectedTab, setSelectedTab] = React.useState<TrendsTabValue>('Chart')
  const [selectedCategory, setSelectedCategory] = React.useState('')

  // Control responsive chart height based on available widget iframe viewport height
  const [viewportHeight, setViewportHeight] = React.useState(window.innerHeight)
  const insightsPadding = isLargeLayout ? 56 : 0
  const availableChartHeight = viewportHeight - 208 // unavailable height on desktop
  const availableListHeight = viewportHeight - (showInsights ? 550 + insightsPadding : 266)

  const [showConnectWidget, setShowConnectWidget] = React.useState(false)
  const hasAggregation = config.show_connections_widget_in_master

  useWidgetLoadTimer({
    widgetName: 'TrendsWidget',
    isLoaded: isWidgetInitialized,
  })
  const filteredBeats = React.useMemo(() => {
    if (!isBeatsLoaded) return []
    return beatStore.getFilteredBeats({ templates: TRENDS_BEAT_TEMPLATES })
  }, [isBeatsLoaded])

  React.useEffect(() => {
    // Setup resize listener for responsive chart and list heights
    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)

    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    if (!categoriesLoaded) {
      loadCategories().finally()
    }
    if (!isTransactionDataLoaded) {
      loadTransactionData().finally()
    }

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Initialize monthlyCategoryTotals, and reload on account or date range change.
  // Depends on primitives rather than `selectedAccounts` / `selectedDateRange`,
  // whose object identities change even when the values do not. (MEW-3910)
  //
  // Both totals are loaded in parallel rather than chaining monthly inside the
  // date-range `.then()`. Chaining defers the monthly request until the first
  // resolves, so a slow superseded range would claim the newest monthly request
  // token and write stale data over the current range. (MEW-3910)
  React.useEffect(() => {
    if (isInitialized && isAccountDataLoaded) {
      beatStore.loadBeats().finally(() => setIsBeatsLoaded(true))
      reloadCategoryTotals(
        selectedAccounts,
        selectedDateRange.start,
        selectedDateRange.end,
      ).finally(() => {
        setIsWidgetInitialized(true)
        onEvent(ANALYTICS_EVENTS.TRENDS_LOAD_WIDGET, {
          ...TRENDS_ANALYTICS_METADATA,
          time_period: `${differenceInCalendarMonths(selectedDateRange.end, selectedDateRange.start)}M`,
        })
      })
    }
  }, [
    isInitialized,
    isAccountDataLoaded,
    selectedAccountGuids.join(','),
    selectedDateRange.start.getTime(),
    selectedDateRange.end.getTime(),
  ])
  React.useEffect(() => {
    // if monthlyCategoryTotals has data, filter transactions
    if (monthlyCategoryTotals.length !== 0) {
      const transactionFilter = {
        accounts: selectedAccountGuids,
        dateRange: { start: selectedDateRange.start, end: selectedDateRange.end },
      }

      // this filter sets sortedTransactions in the transaction store
      setFilter({
        ...transactionFilter,
        custom: (t: Transaction) =>
          Boolean(
            monthlyCategoryTotals.find(
              (cat) =>
                cat.top_level_category_guid === t.top_level_category_guid ||
                cat.category_guid === t.category_guid,
            ),
          ),
      })
    }
  }, [monthlyCategoryTotals])

  const handleDetailOpen = (selected: DetailedCategoryWithTransactions) => {
    setSelectedCategoryData(selected)
  }

  const handleDetailClose = () => {
    setSelectedCategoryData(null)
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_BACK, TRENDS_ANALYTICS_METADATA)
  }

  const handleEmptyStateClick = () => {
    setShowConnectWidget(true)
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_CONNECT_ACCOUNTS)
  }

  const onTabChange = (_event: React.SyntheticEvent, newValue: TrendsTabValue) => {
    setSelectedTab(newValue ?? selectedTab)
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_TOGGLE_VIEW, {
      ...TRENDS_ANALYTICS_METADATA,
      toggleView: newValue,
    })
  }

  const onRangeChanged = (dateRange: DateRange<Date>) => {
    const startDate = addSeconds(dateRange?.[0] as Date, 1)
    const endDate = dateRange?.[1] as Date
    const months = differenceInCalendarMonths(endDate, startDate)

    setSelectedDateRange({ start: startDate, end: endDate })
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_TIME_WINDOW, {
      ...TRENDS_ANALYTICS_METADATA,
      time_period: months + 'M',
    })
  }

  const onAccountsFilterClick = (selectedAccountGuids?: string[]) => {
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_FILTER, {
      ...TRENDS_ANALYTICS_METADATA,
      filterValue: selectedAccountGuids,
    })
  }

  const onClickRow = (guid: string) => {
    setSelectedCategory(guid)
  }

  const onViewAllCategories = () => {
    setSelectedCategory('')
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_ALL_CATEGORIES, TRENDS_ANALYTICS_METADATA)
  }

  if (!isInitialized || !isAccountDataLoaded || !isWidgetInitialized || !monthlyTotalsLoaded)
    return <Loader />

  const isEmptyTrendsState = accounts.length === 0 || monthlyCategoryTotals.length === 0

  const emptyStateProps = buildEmptyStateProps(copy, hasAggregation, handleEmptyStateClick)

  return (
    <WidgetContainer
      calendarActions={{ onRangeChanged }}
      dateRange={selectedDateRange}
      dateRangeVariant={isMobile ? 'timeframetabs' : 'timeframebuttons'}
      onAccountsFilterClick={onAccountsFilterClick}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={copy.title}
    >
      <Stack
        sx={{
          px: isMobile ? 0 : 48,
        }}
      >
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          sx={{ px: isMobile ? 16 : 0, py: 16 }}
        >
          <DateRangeText
            selectedCategoryGuid={selectedCategory}
            selectedDateRange={selectedDateRange}
          />
          <TrendsViewToggle onTabChange={onTabChange} selectedTab={selectedTab} />
        </Stack>
        <Stack alignItems="center" flexDirection="row" sx={{ pb: 8 }} width="100%">
          <Box flexGrow={1}>
            {selectedCategory && (
              <Button onClick={onViewAllCategories} sx={{ p: 0, pr: 5 }}>
                <ArrowBack />
                {copy.all_categories}
              </Button>
            )}
          </Box>
        </Stack>

        {isEmptyTrendsState && (
          <EmptyState
            header={emptyStateProps.header}
            icon="multiline_chart"
            onClick={emptyStateProps.onClickHandler}
            primaryButton={emptyStateProps.primaryButton}
            subText={emptyStateProps.description}
            sx={{ height: availableChartHeight, maxWidth: 432 }}
          />
        )}
        {!isEmptyTrendsState && (
          <Stack flexDirection={isLargeLayout ? 'row' : 'column'} gap={isLargeLayout ? 48 : 16}>
            {selectedTab === 'Chart' && (
              <>
                <Box sx={{ width: isLargeLayout ? '68%' : '100%' }}>
                  <TrendsChartTotals
                    availableHeight={availableChartHeight}
                    minHeight={450}
                    selectedDateRange={selectedDateRange}
                    totals={monthlyCategoryTotals}
                  />
                </Box>
                <Stack gap={16} sx={{ width: isLargeLayout ? '32%' : '100%' }}>
                  {/* Only show insights enabled and there are beats to show */}
                  {showInsights && isBeatsLoaded && filteredBeats.length > 0 && (
                    <InsightsCarousel
                      beats={filteredBeats}
                      onInsightCardClick={onInsightCardClick}
                    />
                  )}
                  <CategoriesList
                    availableHeight={availableListHeight}
                    onCategoryClick={handleDetailOpen}
                  />
                </Stack>
              </>
            )}
            {selectedTab === 'Table' && (
              <Box sx={{ width: '100%' }}>
                <TrendsTableContainer
                  height="unset"
                  onClickRow={onClickRow}
                  selectedCategory={selectedCategory}
                  selectedDateRange={selectedDateRange}
                />
              </Box>
            )}
          </Stack>
        )}
        <Drawer
          ariaLabelClose={copy.close_category_details}
          isOpen={Boolean(selectedCategoryData)}
          onClose={handleDetailClose}
          shouldShowHeaderShadow={true}
          title={selectedCategoryData?.is_income ? copy.category_income : copy.category_spending}
        >
          {selectedCategoryData && <CategoryDetails />}
        </Drawer>
      </Stack>

      <ConnectDrawer
        onClose={() => setShowConnectWidget(false)}
        showConnectWidget={showConnectWidget}
        title={connectCopy.mini_title}
      />
    </WidgetContainer>
  )
}

export default observer(TrendsWidget)
